import type { FetchData } from '@hooks/useDataFetcher';
import { useCallback, useState } from 'react';
import type {
  AuthorizationConfigurationV1,
  BrandCarTypeMappingResponseV1,
  BrandV1,
  CapCodeMappingV1,
  ClientV1,
  ContractLengthV1,
  CustomerTypeV1,
  FeeV1,
  JatoCodeMappingV1,
  MarketV1,
  MileageV1,
  NatcodeMappingV1,
  PreOrderV1,
  SalesModelV1,
  SchwackeCodeMappingV1,
  SelectionCategory,
  SkuCategory,
  SoftOfferPackageV1,
  SoftOfferV1,
  SoftwareOptionPackageV1,
  SoftwareOptionV1,
} from 'types/assortment-admin';
import type { ICommonResponse } from 'types/core';

export type ContentModelManager<T> = {
  item: T | undefined;
  list: T[];
  isLoading: boolean;
  getList: () => Promise<void>;
  getItem: (uid: string) => Promise<T>;
  createItem: (item: T) => Promise<T>;
  updateItem: (item: T) => Promise<T>;
  deleteItem: (item: T) => Promise<T>;
};

export type GenericManager = {
  fees: ContentModelManager<FeeV1>;
  brands: ContentModelManager<BrandV1>;
  clients: ContentModelManager<ClientV1>;
  markets: ContentModelManager<MarketV1>;
  mileages: ContentModelManager<MileageV1>;
  preorders: ContentModelManager<PreOrderV1>;
  softOffers: ContentModelManager<SoftOfferV1>;
  salesModels: ContentModelManager<SalesModelV1>;
  capCodes: ContentModelManager<CapCodeMappingV1>;
  skuCategories: ContentModelManager<SkuCategory>;
  customerTypes: ContentModelManager<CustomerTypeV1>;
  contractLengths: ContentModelManager<ContractLengthV1>;
  softwareOptions: ContentModelManager<SoftwareOptionV1>;
  jatoCodeMappings: ContentModelManager<JatoCodeMappingV1>;
  natcodeMappings: ContentModelManager<NatcodeMappingV1>;
  softOfferPackages: ContentModelManager<SoftOfferPackageV1>;
  selectionCategories: ContentModelManager<SelectionCategory>;
  authConfig: ContentModelManager<AuthorizationConfigurationV1>;
  schwackeCodeMappings: ContentModelManager<SchwackeCodeMappingV1>;
  softwareOptionPackages: ContentModelManager<SoftwareOptionPackageV1>;
  brandCarTypeMappings: ContentModelManager<BrandCarTypeMappingResponseV1>;
};

interface ListResponse<T> extends ICommonResponse {
  data?: T[] | null;
}

interface ItemResponse<T> extends ICommonResponse {
  data: T;
}

const useManage = <T>(
  fetchData: FetchData,
  contentModel: string,
  uid: string,
  transformRequestBody: (item: T) => any = (item) => item,
  transformResponseBody: (item: T) => any = (item) => item,
) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<T[]>([]);
  const [item, setItem] = useState<T>();

  const getItemIndex = useCallback(
    (obj: T) =>
      list.findIndex(
        (comparible) =>
          comparible[uid as keyof typeof comparible] ===
          obj[uid as keyof typeof obj],
      ),
    [list, uid],
  );

  const getList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchData<ListResponse<T>>(
        `/api/manage/${contentModel}`,
      );
      if (
        response?.status !== 200 ||
        (response.data != null && !Array.isArray(response.data))
      ) {
        throw new Error(`Invalid ${contentModel} list response`);
      }
      setList((response.data ?? []).map(transformResponseBody) as T[]);
    } finally {
      setLoading(false);
    }
  }, [fetchData, contentModel, transformResponseBody]);

  const getItem = useCallback(
    async (type: string) => {
      setLoading(true);
      try {
        const response = await fetchData<ItemResponse<T>>(
          `/api/manage/${contentModel}/${encodeURIComponent(type)}`,
        );
        if (response?.status !== 200 || !response.data) {
          throw new Error(`Invalid ${contentModel} item response`);
        }
        if (!list.length) {
          setList([transformResponseBody(response.data)]);
        }

        setItem(transformResponseBody(response.data));
        return transformResponseBody(response.data);
      } finally {
        setLoading(false);
      }
    },
    [contentModel, fetchData, list.length, transformResponseBody],
  );

  const createItem = useCallback(
    async (createble: T) => {
      setLoading(true);
      try {
        const response = await fetchData<ItemResponse<T>>(
          `/api/manage/${contentModel}`,
          undefined,
          'POST',
          transformRequestBody(createble),
          { success: 'Item successfully added' },
        );
        if (response?.status !== 201 || !response.data) {
          throw new Error(`Invalid ${contentModel} create response`);
        }
        setList([transformResponseBody(response.data), ...list]);
        return transformResponseBody(response.data);
      } finally {
        setLoading(false);
      }
    },
    [
      contentModel,
      fetchData,
      list,
      transformRequestBody,
      transformResponseBody,
    ],
  );

  const updateItem = useCallback(
    async (updateable: T) => {
      setLoading(true);
      try {
        const response = await fetchData<ItemResponse<T>>(
          `/api/manage/${contentModel}/${
            updateable[uid as keyof typeof updateable]
          }`,
          undefined,
          'PUT',
          transformRequestBody(updateable),
          { success: 'Item successfully updated' },
        );
        if (response?.status !== 200 || !response.data) {
          throw new Error(`Invalid ${contentModel} update response`);
        }
        const itemIndex = getItemIndex(updateable);

        if (itemIndex > -1) {
          list[itemIndex] = transformResponseBody(response.data);
        }

        setList([...list]);
        return transformResponseBody(response.data);
      } finally {
        setLoading(false);
      }
    },
    [
      contentModel,
      fetchData,
      getItemIndex,
      list,
      uid,
      transformRequestBody,
      transformResponseBody,
    ],
  );

  const deleteItem = useCallback(
    async (deletable: T) => {
      setLoading(true);
      try {
        const response = await fetchData<ItemResponse<T>>(
          `/api/manage/${contentModel}/${
            deletable[uid as keyof typeof deletable]
          }`,
          undefined,
          'DELETE',
          undefined,
          { success: 'Item successfully deleted', error: true, warning: true },
        );
        if (response?.status !== 200) {
          throw new Error(`Invalid ${contentModel} delete response`);
        }
        const itemIndex = getItemIndex(deletable);

        if (itemIndex > -1) {
          list.splice(itemIndex, 1);
          setList([...list]);
        }
        return response.data;
      } finally {
        setLoading(false);
      }
    },
    [contentModel, fetchData, getItemIndex, list, uid],
  );

  return {
    list,
    item,
    isLoading,
    getList,
    getItem,
    createItem,
    updateItem,
    deleteItem,
  };
};

export const useManager = (dataFetcher: FetchData): GenericManager => {
  const brands = useManage<BrandV1>(dataFetcher, 'brands', 'code');
  const brandCarTypeMappings = useManage<BrandCarTypeMappingResponseV1>(
    dataFetcher,
    'brand-car-type-mappings',
    'brand',
  );
  const capCodes = useManage<CapCodeMappingV1>(dataFetcher, 'cap-codes', 'id');
  const mileages = useManage<MileageV1>(dataFetcher, 'mileages', 'id');
  const clients = useManage<ClientV1>(dataFetcher, 'clients', 'id');
  const contractLengths = useManage<ContractLengthV1>(
    dataFetcher,
    'contract-lengths',
    'id',
  );
  const customerTypes = useManage<CustomerTypeV1>(
    dataFetcher,
    'customer-types',
    'code',
  );
  const fees = useManage<FeeV1>(dataFetcher, 'fees', 'internalId');
  const markets = useManage<MarketV1>(dataFetcher, 'markets', 'code');
  const salesModels = useManage<SalesModelV1>(
    dataFetcher,
    'sales-models',
    'type',
  );
  const softOffers = useManage<SoftOfferV1>(
    dataFetcher,
    'soft-offers',
    'internalId',
  );
  const softwareOptionPackages = useManage<SoftwareOptionPackageV1>(
    dataFetcher,
    'software-option-packages',
    'internalId',
  );
  const softwareOptions = useManage<SoftwareOptionV1>(
    dataFetcher,
    'software-options',
    'internalId',
  );
  const softOfferPackages = useManage<SoftOfferPackageV1>(
    dataFetcher,
    'soft-offer-packages',
    'internalId',
  );
  const authConfig = useManage<AuthorizationConfigurationV1>(
    dataFetcher,
    'auth',
    'id',
  );
  const preorders = useManage<PreOrderV1>(
    dataFetcher,
    'preorders',
    'internalId',
  );
  const skuCategories = useManage<SkuCategory>(
    dataFetcher,
    'sku-categories',
    'id',
  );
  const schwackeCodeMappings = useManage<SchwackeCodeMappingV1>(
    dataFetcher,
    'schwacke-code-mappings',
    'id',
  );
  const selectionCategories = useManage<SelectionCategory>(
    dataFetcher,
    'selection-categories',
    'id',
  );
  const jatoCodeMappings = useManage<JatoCodeMappingV1>(
    dataFetcher,
    'jato-code-mappings',
    'id',
  );
  const natcodeMappings = useManage<NatcodeMappingV1>(
    dataFetcher,
    'natcode-mappings',
    'id',
    ({ externalIdentifier, internalIdentifier }) => {
      if (
        !externalIdentifier?.natcode ||
        !internalIdentifier?.pno12 ||
        !internalIdentifier?.modelYear ||
        !internalIdentifier?.marketCode
      ) {
        throw new Error('Nat code mapping is missing required fields');
      }

      return {
        externalIdentifier: { natcode: externalIdentifier.natcode },
        internalIdentifier: {
          pno12: internalIdentifier.pno12,
          modelYear: internalIdentifier.modelYear,
          marketCode: internalIdentifier.marketCode,
          localEditionCode: internalIdentifier.localEditionCode,
        },
      };
    },
  );

  return {
    fees,
    brands,
    brandCarTypeMappings,
    clients,
    markets,
    capCodes,
    mileages,
    preorders,
    authConfig,
    softOffers,
    salesModels,
    customerTypes,
    skuCategories,
    contractLengths,
    softwareOptions,
    jatoCodeMappings,
    natcodeMappings,
    softOfferPackages,
    selectionCategories,
    schwackeCodeMappings,
    softwareOptionPackages,
  };
};
